/**
 * Tiny polar-coordinate helpers shared by the circular Learn diagrams.
 *
 * Convention throughout: angles are in DEGREES, 0° points straight up
 * (12 o'clock) and positive angles run clockwise — the way a beginner reads
 * a clock face, which is who these diagrams are for.
 */

const round = (n: number): number => Math.round(n * 100) / 100;

/** Point on a circle around (cx, cy) at radius r, angle deg (0 = up, cw). */
export function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [round(cx + r * Math.cos(rad)), round(cy + r * Math.sin(rad))];
}

/**
 * SVG path for a ring segment (annulus sector) from angle a0 to a1, clockwise.
 * Used for zodiac wheels and the nakshatra band.
 */
export function ringSegment(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  a0: number,
  a1: number,
): string {
  const [x0, y0] = polar(cx, cy, rOuter, a0);
  const [x1, y1] = polar(cx, cy, rOuter, a1);
  const [x2, y2] = polar(cx, cy, rInner, a1);
  const [x3, y3] = polar(cx, cy, rInner, a0);
  const large = a1 - a0 > 180 ? 1 : 0;
  return (
    `M ${x0} ${y0} A ${rOuter} ${rOuter} 0 ${large} 1 ${x1} ${y1} ` +
    `L ${x2} ${y2} A ${rInner} ${rInner} 0 ${large} 0 ${x3} ${y3} Z`
  );
}

/** SVG path for a plain arc (no closing edges) from a0 to a1. */
export function arcPath(
  cx: number,
  cy: number,
  r: number,
  a0: number,
  a1: number,
  sweep: 0 | 1 = 1,
): string {
  const [x0, y0] = polar(cx, cy, r, a0);
  const [x1, y1] = polar(cx, cy, r, a1);
  const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
}
